import { useCallback, useEffect, useRef, useState } from 'react';

const SAVE_PHASE_EVENT = 'saloni-admin-save-phase';

/** @typedef {'idle' | 'saving' | 'saved'} SavePhase */

/**
 * @param {string} defaultLabel
 * @param {SavePhase} phase
 * @param {string} [savedLabel]
 */
export function saveButtonLabel(defaultLabel, phase, savedLabel = 'Saved ✓') {
  if (phase === 'saving') return 'Saving…';
  if (phase === 'saved') return savedLabel;
  return defaultLabel;
}

/**
 * Shared save-button feedback. Pass `scope` to sync multiple buttons (e.g. Site sub-nav + top bar).
 * @param {{ scope?: string, resetMs?: number }} [options]
 */
export function useSaveButtonLabel(defaultLabel, options = {}) {
  const { scope, resetMs = 2500, savedLabel = 'Saved ✓' } = options;
  const [phase, setPhase] = useState(/** @type {SavePhase} */ ('idle'));
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const broadcast = useCallback(
    (next) => {
      if (!scope) return;
      window.dispatchEvent(new CustomEvent(SAVE_PHASE_EVENT, { detail: { scope, phase: next } }));
    },
    [scope]
  );

  const startSaving = useCallback(() => {
    clearTimer();
    setPhase('saving');
    broadcast('saving');
  }, [broadcast, clearTimer]);

  const markSaved = useCallback(() => {
    clearTimer();
    setPhase('saved');
    broadcast('saved');
    timerRef.current = setTimeout(() => {
      setPhase('idle');
      broadcast('idle');
    }, resetMs);
  }, [broadcast, clearTimer, resetMs]);

  const markFailed = useCallback(() => {
    clearTimer();
    setPhase('idle');
    broadcast('idle');
  }, [broadcast, clearTimer]);

  useEffect(() => {
    if (!scope) return undefined;

    const onPhase = (event) => {
      const detail = event.detail || {};
      if (detail.scope !== scope) return;
      const next = detail.phase;
      if (next !== 'saving' && next !== 'saved' && next !== 'idle') return;
      clearTimer();
      setPhase(next);
      if (next === 'saved') {
        timerRef.current = setTimeout(() => setPhase('idle'), resetMs);
      }
    };

    window.addEventListener(SAVE_PHASE_EVENT, onPhase);
    return () => {
      clearTimer();
      window.removeEventListener(SAVE_PHASE_EVENT, onPhase);
    };
  }, [scope, resetMs, clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return {
    phase,
    startSaving,
    markSaved,
    markFailed,
    label: saveButtonLabel(defaultLabel, phase, savedLabel),
    isSaving: phase === 'saving',
    isSaved: phase === 'saved',
  };
}

export const SITE_SETTINGS_SAVE_SCOPE = 'site-settings';
export const CATALOG_PUBLISH_SAVE_SCOPE = 'catalog-publish';
